// src/App.tsx
import React, { Suspense, useState, useEffect } from 'react';
import logo from './logo.svg';
import './App.css';
import Main from './components/content/Main';
import { BrowserRouter as Router, Route, Routes, useLocation, useParams } from 'react-router-dom';
import './css/bootstrap.min.css';
import './css/style.css';
import './css/style.scss';
import './css/getstarted.css';
import './css/getstarted.scss';
import './css/master.css';
import './css/master.scss';
import './css/now-icon.css';
import './css/nowtvicons.css';
import './App.css';
import './css/combo-pack.css';
import './css/custom-style.css';
import './css/pack-overview.css';
import './css/cookiesbar.css';
import GetStarted from './components/content/GetStarted';
import BoxPlanDetail from './components/content/GetStarted/BoxPlanDetail';
import MinisitePage from './components/content/Minisite';
import HboMaxPage from './components/content/HboMax/HboMaxPage';
import StbHboMaxPage from './components/content/HboMax/StbHboMaxPage';
import NetflixPage from './components/content/Netflix/NetflixPage';
import DisneyPlusPage from './components/content/DisneyPlus/DisneyPlusPage';
import ChannelOverViewPage from './components/content/ChannelOverview';
import { fetchMinisite } from './components/functions/fetchMinisite';
import AvailableChannelPacks from './components/content/AvailableChannelPacks';
import Recommendation from './components/content/Recommendation';
import BlogPage from './components/content/Blog';
import Article from './components/content/Blog/Article';
import BlogFilter from './components/content/Blog/BlogFilter';
import Support from './components/content/Support/Support';
import SupportItem from './components/content/Support/SupportItem';
import SupportFrequentlyReadArticles from './components/content/Support/SupportFrequentlyReadArticles';
import SupportSearchResult from './components/content/Support/SupportSearchResult';
import HktAiAssistant from './components/content/HktAiAssistant';

import { ENV } from './config';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import CookiesBar from './components/CookiesBar/CookiesBar';
import { useFooterData } from './hooks/useFooterData';
import { MinisiteLayoutProvider, useMinisiteLayout } from './contexts/MinisiteLayoutContext';
import { AuthProvider } from './contexts/AuthProvider';
import { loadHomeBootstrap } from './utils/homeBootstrap';
import Home from './components/content/Home/Home';
import AboutUs from './components/content/AboutUs';
import ChatBubble from './components/ChatBubble/ChatBubble';
import ContactUs from './components/content/ContactUs';
import TermsAndConditions from './components/content/TermsAndConditions';
import LegalComplianceInformation from './components/content/LegalComplianceInformation';
import OTTSubscriberEnquiry from './components/content/OTTSubscriberEnquiry';
import SubscriberEnquiry from './components/content/SubscriberEnquiry';
import Sitemap from './components/content/Sitemap';
import OnlineSalesTeam from './components/content/OnlineSalesTeam';
import BroadcastLocation from './components/content/BroadcastLocation';
import { fetchBroadcastLocation } from './components/functions/fetchBroadcastLocation';
import Advertiser from './components/content/Advertiser/Advertiser';
import Business from './components/content/Business/Business';
import Error404 from './components/content/Error404';
import RedeemCode from './components/content/RedeemCode';
import MyAccountWrapper from './components/content/MyAccount/MyAccountWrapper';

// Lazy-load MyAccount page components so their module-scope reads of
// window.__NOWTV_AUTH__ happen AFTER MyAccountWrapper sets the global.
const MyAccount = React.lazy(() => import('./components/content/MyAccount/MyAccount/MyAccount'));
const SubscriptionDetails = React.lazy(() => import('./components/content/MyAccount/component/AccountnSubscriptions/SubscriptionDetailsnRenewalDates/SubscriptionDetailsnRenewalDates'));
const EditCommunicationPreferences = React.lazy(() => import('./components/content/MyAccount/component/CommunicationPreferences/EditCommunicationPreferences/EditCommunicationPreferences'));
const ChangePaymentMethod = React.lazy(() => import('./components/content/MyAccount/component/PaymentMethodsnBillings/Change/ChangePaymentMethod'));
const PaymentHistory = React.lazy(() => import('./components/content/MyAccount/component/PaymentMethodsnBillings/PaymentHistory'));
const ChangeBillSettings = React.lazy(() => import('./components/content/MyAccount/component/PaymentMethodsnBillings/Change/ChangeBillSettings'));
const EditManageDevice = React.lazy(() => import('./components/content/MyAccount/component/ManageDevice/EditManageDevice'));
//remove all console logs
if (ENV === 'production' || ENV === 'preproduction') {
   console.log = function() {}
}

function PathRouter(): React.ReactElement | null {
  const { '*': rawPath } = useParams<{ '*': string }>();
  const pathname = rawPath?.replace(/\/$/, '');
  const [content, setContent] = useState<React.ReactElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await fetchBroadcastLocation("en");
        let renderContent: React.ReactElement | null = null;
        if (data) {
          const originalSlug = data?.attributes?.Original?.Slug;
          const specialSlug = data?.attributes?.Special?.Slug;
          
          if (pathname === originalSlug || pathname === specialSlug) {
            renderContent = <BroadcastLocation />;
          } else {
            renderContent = <MinisitePage />;
          }
        } else {
          renderContent = <MinisitePage />;
        }
        setContent(renderContent);
      } catch (err) {
        setError('Failed to load page content');
        setContent(<MinisitePage />);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [pathname]);
  if (loading) {
    return <div className="loading">Loading...</div> as React.ReactElement;
  }
  if (error) {
    return <div className="error">{error}</div> as React.ReactElement;
  }
  return content || <MinisitePage />;
}

function AppContent() {
  const footerData = useFooterData();
  const { headerMode, footerMode } = useMinisiteLayout();
  const { pathname } = useLocation();

  const showChatBubble = pathname === '/' || 
                        pathname === '/get-started' || 
                        pathname === '/get-started/' ||
                        pathname === '/get-started-box' || 
                        pathname === '/get-started-box/' ||
                        pathname === '/support' ||
                        pathname === '/support/';

  return (
    <>
      <Header mode={headerMode} />
      <main id="main" role="main">
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/get-started" element={<GetStarted />} />
        <Route path="/get-started-box" element={<GetStarted />} />
        <Route path="/combo-pack/:id" element={<BoxPlanDetail />} />
        <Route path="/hktaiassistant" element={<HktAiAssistant />} />
        <Route path="/nowtv-hbomax" element={<HboMaxPage />} />
        <Route path="/stb-hbomax" element={<StbHboMaxPage />} />
        <Route path="/nowtv-netflix" element={<NetflixPage />} />
        <Route path="/nowtv-disneyplus" element={<DisneyPlusPage />} />
        <Route path="/my-account" element={<MyAccountWrapper><Suspense fallback={<div className="loading">Loading...</div>}><MyAccount /></Suspense></MyAccountWrapper>} />
        <Route path="/subscription-details" element={<MyAccountWrapper><Suspense fallback={<div className="loading">Loading...</div>}><SubscriptionDetails /></Suspense></MyAccountWrapper>} />
        <Route path="/communication-preferences" element={<MyAccountWrapper><Suspense fallback={<div className="loading">Loading...</div>}><EditCommunicationPreferences /></Suspense></MyAccountWrapper>} />
        <Route path="/change-creditcard" element={<MyAccountWrapper><Suspense fallback={<div className="loading">Loading...</div>}><ChangePaymentMethod /></Suspense></MyAccountWrapper>} />
        <Route path="/payment-history" element={<MyAccountWrapper><Suspense fallback={<div className="loading">Loading...</div>}><PaymentHistory /></Suspense></MyAccountWrapper>} />
        <Route path="/bill-settings" element={<MyAccountWrapper><Suspense fallback={<div className="loading">Loading...</div>}><ChangeBillSettings /></Suspense></MyAccountWrapper>} />
        <Route path="/manage-device" element={<MyAccountWrapper><Suspense fallback={<div className="loading">Loading...</div>}><EditManageDevice /></Suspense></MyAccountWrapper>} />
        <Route path="/*" element={<PathRouter />} />
        <Route path="/channel-overview" element={<ChannelOverViewPage />} />
        <Route path="/available-channel-packs" element={<AvailableChannelPacks/>} />
        <Route path='/subscribe_recommendation' element={<Recommendation />} />
        <Route path='/upgrade_recommendation' element={<Recommendation />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<Article />} />
        <Route path="/blog/filter" element={<BlogFilter />} />
        <Route path="/blog/filter/:cid" element={<BlogFilter />} />
        <Route path='/support' element={<Support/>} />
        <Route path='/support/:slug' element={<SupportItem/>} />
        <Route path='/frequently-read-articles' element={<SupportFrequentlyReadArticles/>} />
        <Route path='/support-search-result' element={<SupportSearchResult />} />
        <Route path='/about' element={<AboutUs />} />
        <Route path='/policy' element={<LegalComplianceInformation />} />
        <Route path='/non_discrimination_and_non_harassment_statement' element={<LegalComplianceInformation />} />
        <Route path='/fair-use-policy' element={<LegalComplianceInformation />} />
        <Route path='/disclaimer' element={<LegalComplianceInformation />} />
        <Route path='/contact-us' element={<ContactUs />} />
        <Route path='/terms-and-conditions' element={<TermsAndConditions />} />
        <Route path='/enquiry' element={<OTTSubscriberEnquiry />} />
        <Route path='/subscriber-enquiry' element={<SubscriberEnquiry />} />
        <Route path='/sitemap' element={<Sitemap />} />
        <Route path='/online-sales-team' element={<OnlineSalesTeam />} />
        <Route path='/advertiser' element={<Advertiser />} />
        <Route path='/business' element={<Business />} />
        <Route path='/404' element={<Error404 />} />
        <Route path='/redeem-code' element={<RedeemCode />} />
      </Routes>
      </main>
      {footerMode !== 'hidden' && footerData.showFooter && !footerData.loading && (
        <Footer
          status={footerMode === 'footer-only' ? 'copyrightOnly' : 'normal'}
          bottomMenuLinks={footerData.bottomMenuLinks}
          copyrightText={footerData.copyrightText}
          dpmsText={footerData.dpmsText}
          footerTagline={footerData.footerTagline}
          language={footerData.language}
        />
      )}
      <ChatBubble isVisible={showChatBubble} />
      <CookiesBar />
    </>
  );
}

function App() {
  const [bootstrapData, setBootstrapData] = useState<Record<string, unknown>>({});
  const [bootstrapReady, setBootstrapReady] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadBootstrap = async () => {
      try {
        const data = await loadHomeBootstrap();
        if (mounted) {
          setBootstrapData(data);
        }
      } finally {
        if (mounted) {
          setBootstrapReady(true);
        }
      }
    };

    loadBootstrap();

    return () => {
      mounted = false;
    };
  }, []);

  if (!bootstrapReady) {
    return null;
  }

  return (
    <Router>
      <AuthProvider bootstrapData={bootstrapData}>
        <MinisiteLayoutProvider>
          <AppContent />
        </MinisiteLayoutProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;